import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import ToolNav from '@/components/site/ToolNav';
import SEO from '@/components/site/SEO';
import { Check, Copy, Printer, AlertCircle, ChevronDown, ChevronUp, Link } from 'lucide-react';

/* ──────────────────────────────────────────────
   DATA
────────────────────────────────────────────── */

const PROFILES = [
  { id: 'salaried', label: 'Salaried Individual' },
  { id: 'proprietorship', label: 'Businessman — Proprietorship' },
  { id: 'llp', label: 'Businessman — LLP / Partnership' },
  { id: 'pvtltd', label: 'Businessman — Pvt Ltd' },
  { id: 'professional', label: 'Professional' },
  { id: 'nri', label: 'NRI' },
  { id: 'merchant_navy', label: 'Merchant Navy' },
];

const PRODUCTS = [
  { id: 'personal_loan', label: 'Personal Loan' },
  { id: 'business_loan', label: 'Business Loan' },
  { id: 'home_loan_purchase', label: 'Home Loan — Purchase' },
  { id: 'commercial_purchase', label: 'Commercial Purchase' },
  { id: 'balance_transfer', label: 'Home Loan — Balance Transfer' },
  { id: 'mortgage', label: 'Mortgage Loan' },
];

// Products that need a property scenario sub-selection
const PRODUCTS_WITH_PROPERTY = ['home_loan_purchase', 'commercial_purchase'];
const PRODUCTS_WITH_BT = ['balance_transfer'];

const PROPERTY_SCENARIOS = [
  { id: 'builder_under_construction', label: 'Builder — Under Construction' },
  { id: 'builder_ready', label: 'Builder — Ready / OC Received' },
  { id: 'resale', label: 'Resale' },
];

const BT_SCENARIOS = [
  { id: 'bt_ready', label: 'Ready Property BT' },
  { id: 'bt_under_construction', label: 'Under-Construction BT' },
];

// KYC — common for all
const KYC_DOCS = [
  'PAN card and Aadhaar card of applicant and co-applicants',
  'Residential proof — latest electricity bill (if rented house: rent agreement also required)',
  'Passport size photo of applicant and co-applicant',
  'Relationship proof of co-applicant (e.g., marriage certificate) — if applicable',
];

// Income docs by profile
const INCOME_DOCS = {
  salaried: [
    'Office ID',
    'Appointment Letter',
    "Six months' salary slips",
    '12 months salary account statement',
    'Form 16 Part A and B of two years',
    'ITR acknowledgment copy for last two years (if available)',
  ],
  proprietorship: [
    'Three year ITR with acknowledgment page, computation of income, balance sheet, profit and loss',
    'One year bank statement (saving / current)',
    'Gumasta License / GST Certificate / industry-wise business proof / UDYAM',
    'Business address proof (electricity bill / rent agreement)',
  ],
  llp: [
    "Partner's own ITRs with computation of income — CA verified",
    "Company's ITRs and 26AS for last three years — CA verified, with balance sheet, profit & loss, and computation",
    'LLP agreement',
    'Company PAN card',
    'GST certificate and related GST documents',
  ],
  pvtltd: [
    'MOA and AOA',
    'Company PAN card',
    'Company 26AS for last three years',
    'GSTR-3B for last one year',
    'Company ITR — if audited, complete audit report with computation, balance sheet and P&L for last three years',
    'Company bank statement (company banking)',
    "Individual applicant's (Director's) ITR with computation of income — last three years",
    "Individual applicant's (Director's) 26AS — last three years",
  ],
  // Professional running as Proprietorship / Individual
  professional_proprietorship: [
    'Three year ITR with acknowledgment page, computation of income, balance sheet, profit and loss',
    'One year bank statement (saving / current)',
    'Professional degree / credential proof (e.g., medical degree, artist card)',
    'Business address proof (electricity bill / rent agreement)',
  ],
  // Professional running as LLP / Company — same as LLP profile
  professional_llp: [
    "Partner's own ITRs with computation of income — CA verified",
    "Company's ITRs and 26AS for last three years — CA verified, with balance sheet, profit & loss, and computation",
    'LLP agreement',
    'Company PAN card',
    'GST certificate and related GST documents',
    'Professional degree / credential proof of the applicant partner (e.g., medical degree, artist card)',
  ],
  nri: [
    'Work contract letter',
    'Passport',
    'Visa',
    'Overseas CIBIL (credit) report',
    'Bank account statement',
    'Address proof abroad',
    "Salary slips — last 6 months",
  ],
  merchant_navy: [
    'Passport',
    'Visa',
    'CDC (Continuous Discharge Certificate)',
    "Last two years' contract letters",
    'Last 6 months salary slips',
    'Bank account statement',
    'Future contract letter (if available)',
  ],
};

// Profile-specific notes shown alongside the generated list
const PROFILE_NOTES = {
  merchant_navy:
    'Note: Merchant Navy professionals typically earn for ~6 months on ship and 6 months on land. Income is assessed as a yearly average — earnings accrue only during sailing months.',
};

// Product docs by product + optional scenario
function getProductDocs(product, scenario) {
  if (product === 'personal_loan' || product === 'business_loan') {
    return []; // KYC + income only
  }
  if (product === 'home_loan_purchase' || product === 'commercial_purchase') {
    if (scenario === 'builder_under_construction') {
      return [
        'Final agreement (if already executed) — otherwise draft agreement',
        "Builder's cost sheet",
        'Payment receipts (if any payment made)',
        'TDS challan (at later stage)',
        'Demand letter from builder',
      ];
    }
    if (scenario === 'builder_ready') {
      return [
        'All relevant purchase documents',
        'OC (Occupation Certificate) copy',
        "Builder's cost sheet",
        'Payment receipts',
        'Sale agreement',
      ];
    }
    if (scenario === 'resale') {
      return [
        "Previous sellers' chain agreements / sale agreement",
        'Share certificate',
        'Property tax receipt',
      ];
    }
    return [];
  }
  if (product === 'balance_transfer') {
    if (scenario === 'bt_ready') {
      return [
        'Sale agreement with OC (Occupation Certificate)',
        'Loan account statement',
        'Sanction letter',
        'LOD (List of Documents) issued after sanction',
        'Foreclosure letter',
      ];
    }
    if (scenario === 'bt_under_construction') {
      return [
        'Sale agreement',
        'Payment receipts',
        "Builder's cost sheet",
        'Builder ledger of payments done',
        'Loan account statement',
        'Sanction letter',
        'LOD (List of Documents) issued after sanction',
        'Foreclosure letter',
      ];
    }
    return [];
  }
  if (product === 'mortgage') {
    return [
      'Ownership / title documents of the property to be mortgaged',
      'Property tax receipts (latest)',
      'Property address proof',
    ];
  }
  return [];
}

// Problem case definitions
const PROBLEMS = [
  {
    id: 'no_form16',
    label: 'Form 16 not available (salaried)',
    applicableProfiles: ['salaried'],
    docs: [
      'Form 26AS for last two years',
      'ITR acknowledgment copy for last two years',
    ],
  },
  {
    id: 'rented',
    label: 'Rented residence',
    applicableProfiles: null, // all profiles
    docs: ['Rent agreement for current residence'],
  },
  {
    id: 'gst_registered',
    label: 'GST registered business',
    applicableProfiles: ['professional'], // proprietorship handles GST upfront
    docs: [
      'GST certificate',
      'GSTR-3B for last 1 year',
      '26AS for last three years',
    ],
  },
];

/* ──────────────────────────────────────────────
   HELPERS
────────────────────────────────────────────── */
function buildDocList(profile, product, scenario, { pvtltdSalary = false, proprietorshipGST = false, professionalType = null } = {}) {
  const sections = [];

  sections.push({ heading: 'KYC Documents', docs: KYC_DOCS });

  // Professional resolves to a sub-key based on the chosen sub-type
  const incomeKey = profile === 'professional'
    ? (professionalType === 'llp' ? 'professional_llp' : 'professional_proprietorship')
    : profile;

  const income = [...(INCOME_DOCS[incomeKey] || [])];

  // Pvt Ltd: add director salary slips if they draw salary from company
  if (profile === 'pvtltd' && pvtltdSalary) {
    income.push("Salary slips of director/applicant from the company — last 6 months");
  }

  // Proprietorship: add GST docs if registered
  if (profile === 'proprietorship' && proprietorshipGST) {
    income.push('GST certificate');
    income.push('GSTR-3B for last 1 year');
    income.push('26AS for last three years');
  }

  if (income.length) {
    sections.push({ heading: 'Income Documents', docs: income });
  }

  const productDocs = getProductDocs(product, scenario);
  if (productDocs.length) {
    const productLabel = PRODUCTS.find((p) => p.id === product)?.label || 'Property / Loan Documents';
    sections.push({ heading: `${productLabel} — Property Documents`, docs: productDocs });
  }

  return sections;
}

function sectionsToText(sections, problemDocs, note) {
  let num = 1;
  let text = '';
  sections.forEach((sec) => {
    text += `\n${sec.heading}\n`;
    sec.docs.forEach((d) => {
      text += `${num}. ${d}\n`;
      num++;
    });
  });
  if (problemDocs.length) {
    text += `\nAdditional Documents\n`;
    problemDocs.forEach((d) => {
      text += `${num}. ${d}\n`;
      num++;
    });
  }
  if (note) {
    text += `\n📌 ${note}\n`;
  }
  return text.trim();
}

/* ──────────────────────────────────────────────
   COMPONENT
────────────────────────────────────────────── */
export default function DocumentsRequired() {
  const location = useLocation();
  const navigate = useNavigate();

  const [profile, setProfile] = useState('');
  const [product, setProduct] = useState('');
  const [scenario, setScenario] = useState('');
  const [generated, setGenerated] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  // Pvt Ltd: does the director draw salary from the company?
  const [pvtltdSalary, setPvtltdSalary] = useState(null); // null | true | false
  // Proprietorship: is the business GST registered?
  const [proprietorshipGST, setProprietorshipGST] = useState(null); // null | true | false
  // Professional: running as proprietorship/individual or as LLP/company?
  const [professionalType, setProfessionalType] = useState(null); // null | 'proprietorship' | 'llp'
  const outputRef = useRef(null);

  // On mount: read query params and auto-generate if valid
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qProfile = params.get('profile') || '';
    const qProduct = params.get('product') || '';
    const qScenario = params.get('scenario') || '';

    const validProfile = PROFILES.some((p) => p.id === qProfile);
    const validProduct = PRODUCTS.some((p) => p.id === qProduct);

    if (validProfile && validProduct) {
      const needsP = PRODUCTS_WITH_PROPERTY.includes(qProduct);
      const needsBT = PRODUCTS_WITH_BT.includes(qProduct);
      const needsSc = needsP || needsBT;
      const allScenarios = [...PROPERTY_SCENARIOS, ...BT_SCENARIOS];
      const validScenario = !needsSc || allScenarios.some((s) => s.id === qScenario);

      if (validScenario) {
        setProfile(qProfile);
        setProduct(qProduct);
        setScenario(needsSc ? qScenario : '');
        setGenerated(true);
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsPropertyScenario = PRODUCTS_WITH_PROPERTY.includes(product);
  const needsBTScenario = PRODUCTS_WITH_BT.includes(product);
  const needsScenario = needsPropertyScenario || needsBTScenario;
  const scenarioOptions = needsBTScenario ? BT_SCENARIOS : PROPERTY_SCENARIOS;

  // Determine applicable problems for current profile
  const applicableProblems = PROBLEMS.filter(
    (p) => p.applicableProfiles === null || (profile && p.applicableProfiles.includes(profile))
  );

  // Build sections
  const sections = generated
    ? buildDocList(profile, product, scenario, { pvtltdSalary, proprietorshipGST, professionalType })
    : [];
  const profileNote = generated ? PROFILE_NOTES[profile] : null;

  // Problem docs
  const problemDocs = selectedProblems.flatMap(
    (pid) => PROBLEMS.find((p) => p.id === pid)?.docs || []
  );

  // Total count
  const totalDocs =
    sections.reduce((a, s) => a + s.docs.length, 0) + problemDocs.length;

  function handleGenerate() {
    if (!profile) {
      setValidationError('Please select an applicant profile.');
      return;
    }
    if (profile === 'pvtltd' && pvtltdSalary === null) {
      setValidationError('Please answer the director salary question before generating.');
      return;
    }
    if (profile === 'proprietorship' && proprietorshipGST === null) {
      setValidationError('Please answer the GST registration question before generating.');
      return;
    }
    if (profile === 'professional' && professionalType === null) {
      setValidationError('Please select how the professional is running their practice before generating.');
      return;
    }
    if (!product) {
      setValidationError('Please select a loan product.');
      return;
    }
    if (needsScenario && !scenario) {
      setValidationError('Please select a property scenario.');
      return;
    }
    setValidationError('');
    setGenerated(true);
    setSelectedProblems([]);
    setShowProblems(false);

    // Update URL with current selections
    const params = new URLSearchParams();
    params.set('profile', profile);
    params.set('product', product);
    if (needsScenario && scenario) params.set('scenario', scenario);
    navigate({ search: params.toString() }, { replace: true });

    setTimeout(() => outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }

  function handleReset() {
    setProfile('');
    setProduct('');
    setScenario('');
    setGenerated(false);
    setValidationError('');
    setSelectedProblems([]);
    setShowProblems(false);
    setCopied(false);
    setPvtltdSalary(null);
    setProprietorshipGST(null);
    setProfessionalType(null);
    setLinkCopied(false);
    navigate({ search: '' }, { replace: true });
  }

  function handleCopy() {
    const text = sectionsToText(sections, problemDocs, profileNote);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleShareLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  function handlePrint() {
    const profileLabel = PROFILES.find((p) => p.id === profile)?.label || '';
    const productLabel = PRODUCTS.find((p) => p.id === product)?.label || '';
    const scenarioLabel = scenario
      ? [...PROPERTY_SCENARIOS, ...BT_SCENARIOS].find((s) => s.id === scenario)?.label
      : '';

    const allSections = [
      ...sections,
      ...(problemDocs.length > 0 ? [{ heading: 'Additional Documents', docs: problemDocs, isAdditional: true }] : []),
    ];

    let num = 1;
    const sectionsHtml = allSections.map((sec, si) => {
      const docsHtml = sec.docs.map((doc) => {
        const n = num++;
        return `<div class="doc-item">
          <div class="checkbox"></div>
          <div class="doc-text"><span class="doc-num">${n}.</span>${doc}</div>
        </div>`;
      }).join('');
      return `<div class="section${si > 0 ? ' section-border' : ''}">
        <div class="section-heading${sec.isAdditional ? ' heading-amber' : ''}">${sec.heading}</div>
        <div class="docs-list">${docsHtml}</div>
      </div>`;
    }).join('');

    const noteHtml = profileNote
      ? `<div class="note">📌 ${profileNote}</div>`
      : '';

    const dateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Documents Required — Finirio</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',Arial,sans-serif;font-size:13px;color:#1e293b;background:#fff;padding:32px 40px;max-width:800px;margin:0 auto}
.header{display:flex;align-items:center;justify-content:space-between;border-bottom:2.5px solid #1e73be;padding-bottom:14px;margin-bottom:20px}
.logo{font-size:22px;font-weight:800;color:#1e73be;letter-spacing:-0.5px}
.logo span{color:#0f3d78}
.header-meta{text-align:right;font-size:11px;color:#64748b;line-height:1.6}
.title-block{margin-bottom:18px}
.title-block h1{font-size:18px;font-weight:700;color:#0f172a}
.badge{display:inline-block;background:#1e73be;color:#fff;font-size:11px;font-weight:600;border-radius:20px;padding:2px 10px;margin-left:8px;vertical-align:middle}
.subtitle{font-size:12px;color:#64748b;margin-top:5px}
.card{border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;margin-bottom:16px}
.section-border{border-top:1px solid #e2e8f0}
.section-heading{background:#f0f7ff;color:#1e73be;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;padding:8px 14px}
.heading-amber{background:#fff8f0;color:#b45309}
.docs-list{padding:8px 14px}
.doc-item{display:flex;align-items:flex-start;gap:10px;padding:7px 0;border-bottom:1px solid #f1f5f9}
.doc-item:last-child{border-bottom:none}
.checkbox{flex-shrink:0;width:16px;height:16px;border:1.5px solid #94a3b8;border-radius:3px;margin-top:2px}
.doc-text{line-height:1.5;color:#334155}
.doc-num{color:#1e73be;font-weight:600;margin-right:4px}
.note{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:12px 16px;font-size:12px;color:#1e40af;line-height:1.6;margin-bottom:16px}
.footer{border-top:1px solid #e2e8f0;padding-top:12px;font-size:10px;color:#94a3b8;display:flex;justify-content:space-between;margin-top:8px}
@media print{body{padding:20px}@page{margin:12mm;size:A4}}
</style>
</head>
<body>
<div class="header">
  <div class="logo">FINI<span>RIO</span></div>
  <div class="header-meta"><div>finirio.com</div><div>${dateStr}</div></div>
</div>
<div class="title-block">
  <h1>Documents Required <span class="badge">${totalDocs} documents</span></h1>
  <div class="subtitle">${profileLabel}${productLabel ? ' · ' + productLabel : ''}${scenarioLabel ? ' · ' + scenarioLabel : ''}</div>
</div>
<div class="card">${sectionsHtml}</div>
${noteHtml}
<div class="footer">
  <span>Generated by Finirio — India's trusted home loan advisor</span>
  <span>This list is indicative. Additional documents may be required by the bank.</span>
</div>
<script>window.onload=function(){window.print()}<\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  }

  function toggleProblem(pid) {
    setSelectedProblems((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]
    );
  }

  // When product changes, reset scenario
  function handleProductChange(val) {
    setProduct(val);
    setScenario('');
    setGenerated(false);
  }

  // When profile changes, reset generation and profile-specific answers
  function handleProfileChange(val) {
    setProfile(val);
    setGenerated(false);
    setSelectedProblems([]);
    setPvtltdSalary(null);
    setProprietorshipGST(null);
    setProfessionalType(null);
  }

  let globalNum = 1;

  return (
    <main>
      <SEO
        title="Documents Required for Loan | Finirio"
        description="Find out exactly which documents you need for your home loan, mortgage, business loan or personal loan application. Generate a personalised checklist based on your profile."
        path="/documents"
        keywords="loan documents required, home loan documents, mortgage documents, salaried documents, business loan documents checklist"
      />
      <PageBanner title="Documents Required" crumbs={['Calci and Docs', 'Documents Required']} />
      <ToolNav />

      {/* ── Selector Section ── */}
      <section className="py-14 bg-white print:hidden">
        <div className="max-w-[860px] mx-auto px-4 lg:px-6">
          <p className="text-slate-600 mb-8 text-[15px] leading-relaxed">
            Select your applicant profile and loan product below, then click{' '}
            <strong>Generate List</strong> to see the exact documents you need to submit.
          </p>

          <div className="space-y-8">
            {/* Profile */}
            <div>
              <label className="block text-[13px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                Applicant Profile
              </label>
              <div className="flex flex-wrap gap-2">
                {PROFILES.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleProfileChange(p.id)}
                    className={`px-4 py-2 rounded-full border text-[14px] font-medium transition-all ${
                      profile === p.id
                        ? 'border-transparent bg-gradient-to-br from-[#1e73be] to-[#0b2545] text-white shadow-md'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-[#c9a24b]/60 hover:text-[#0b2545] hover:shadow-sm'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Pvt Ltd — director salary question */}
            {profile === 'pvtltd' && (
              <div className="rounded-xl px-5 py-4 border border-[#c9a24b]/35" style={{ backgroundColor: '#FBF9F4' }}>
                <p className="text-[14px] font-semibold text-slate-700 mb-3">
                  Does the director/applicant draw a monthly salary from the company?
                </p>
                <div className="flex gap-2">
                  {[{ val: true, label: 'Yes' }, { val: false, label: 'No' }].map(({ val, label }) => (
                    <button
                      key={String(val)}
                      onClick={() => { setPvtltdSalary(val); setGenerated(false); }}
                      className={`px-5 py-2 rounded-full border text-[14px] font-medium transition-all ${
                        pvtltdSalary === val
                          ? 'border-transparent bg-gradient-to-br from-[#1e73be] to-[#0b2545] text-white shadow-md'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-[#c9a24b]/60 hover:text-[#0b2545] hover:shadow-sm'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {pvtltdSalary === true && (
                  <p className="text-[12px] text-[#a9812f] mt-2">Salary slips (last 6 months) will be added to the list.</p>
                )}
              </div>
            )}

            {/* Professional — practice structure question */}
            {profile === 'professional' && (
              <div className="rounded-xl px-5 py-4 border border-[#c9a24b]/35" style={{ backgroundColor: '#FBF9F4' }}>
                <p className="text-[14px] font-semibold text-slate-700 mb-3">
                  How is the professional running their practice?
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { val: 'proprietorship', label: 'Proprietorship / Individual' },
                    { val: 'llp', label: 'LLP / Company' },
                  ].map(({ val, label }) => (
                    <button
                      key={val}
                      onClick={() => { setProfessionalType(val); setGenerated(false); }}
                      className={`px-5 py-2 rounded-full border text-[14px] font-medium transition-all ${
                        professionalType === val
                          ? 'border-transparent bg-gradient-to-br from-[#1e73be] to-[#0b2545] text-white shadow-md'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-[#c9a24b]/60 hover:text-[#0b2545] hover:shadow-sm'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {professionalType === 'llp' && (
                  <p className="text-[12px] text-[#a9812f] mt-2">LLP income documents will be shown along with professional degree proof.</p>
                )}
                {professionalType === 'proprietorship' && (
                  <p className="text-[12px] text-[#a9812f] mt-2">Individual / proprietorship income documents will be shown along with professional degree proof.</p>
                )}
              </div>
            )}

            {/* Proprietorship — GST question */}
            {profile === 'proprietorship' && (
              <div className="rounded-xl px-5 py-4 border border-[#c9a24b]/35" style={{ backgroundColor: '#FBF9F4' }}>
                <p className="text-[14px] font-semibold text-slate-700 mb-3">
                  Is the business GST registered?
                </p>
                <div className="flex gap-2">
                  {[{ val: true, label: 'Yes' }, { val: false, label: 'No' }].map(({ val, label }) => (
                    <button
                      key={String(val)}
                      onClick={() => { setProprietorshipGST(val); setGenerated(false); }}
                      className={`px-5 py-2 rounded-full border text-[14px] font-medium transition-all ${
                        proprietorshipGST === val
                          ? 'border-transparent bg-gradient-to-br from-[#1e73be] to-[#0b2545] text-white shadow-md'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-[#c9a24b]/60 hover:text-[#0b2545] hover:shadow-sm'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {proprietorshipGST === true && (
                  <p className="text-[12px] text-[#a9812f] mt-2">GST certificate, GSTR-3B, and 26AS will be added to the list.</p>
                )}
              </div>
            )}

            {/* Product */}
            <div>
              <label className="block text-[13px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                Loan Product
              </label>
              <div className="flex flex-wrap gap-2">
                {PRODUCTS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleProductChange(p.id)}
                    className={`px-4 py-2 rounded-full border text-[14px] font-medium transition-all ${
                      product === p.id
                        ? 'border-transparent bg-gradient-to-br from-[#1e73be] to-[#0b2545] text-white shadow-md'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-[#c9a24b]/60 hover:text-[#0b2545] hover:shadow-sm'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Property / BT Scenario (conditional) */}
            {needsScenario && (
              <div>
                <label className="block text-[13px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                  {needsBTScenario ? 'Balance Transfer Scenario' : 'Property Scenario'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {scenarioOptions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setScenario(s.id); setGenerated(false); }}
                      className={`px-4 py-2 rounded-full border text-[14px] font-medium transition-all ${
                        scenario === s.id
                          ? 'border-transparent bg-gradient-to-br from-[#1e73be] to-[#0b2545] text-white shadow-md'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-[#c9a24b]/60 hover:text-[#0b2545] hover:shadow-sm'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Validation error */}
            {validationError && (
              <div className="flex items-center gap-2 text-red-600 text-[14px] bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {validationError}
              </div>
            )}

            {/* Generate button */}
            <div className="flex gap-3">
              <button
                onClick={handleGenerate}
                className="btn-lux !px-8 !py-3 active:scale-95"
              >
                Generate List
              </button>
              {generated && (
                <button
                  onClick={handleReset}
                  className="px-6 py-3 rounded-lg border border-slate-300 text-slate-600 font-medium text-[14px] hover:bg-slate-50 transition-all"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Generated Output ── */}
      {generated && (
        <section ref={outputRef} className="pb-16 print:bg-white print:pb-0" style={{ backgroundColor: '#FBF9F4' }}>
          <div className="max-w-[860px] mx-auto px-4 lg:px-6">
            {/* Header bar */}
            <div className="flex items-center justify-between pt-10 pb-5 print:pt-4">
              <div>
                <h2 className="font-heading text-[22px] font-bold text-[#0b2545]">
                  Documents Required
                </h2>
                <p className="text-slate-500 text-[13px] mt-1">
                  {PROFILES.find((p) => p.id === profile)?.label} ·{' '}
                  {PRODUCTS.find((p) => p.id === product)?.label}
                  {scenario
                    ? ` · ${[...PROPERTY_SCENARIOS, ...BT_SCENARIOS].find((s) => s.id === scenario)?.label}`
                    : ''}
                  {' '}— {totalDocs} document{totalDocs !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex gap-2 print:hidden">
                <button
                  onClick={handleShareLink}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-[13px] font-medium hover:bg-slate-50 transition-all"
                >
                  {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Link className="w-4 h-4" />}
                  {linkCopied ? 'Link copied!' : 'Share link'}
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-[13px] font-medium hover:bg-slate-50 transition-all"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy list'}
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-[13px] font-medium hover:bg-slate-50 transition-all"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>

            {/* Document list card */}
            <div className="card-lux overflow-hidden print:border-0 print:shadow-none print:rounded-none">
              {(() => {
                globalNum = 1;
                return sections.map((sec, si) => (
                  <div key={si} className={si > 0 ? 'border-t border-slate-100' : ''}>
                    <div className="px-6 py-3 print:px-0 border-l-2 border-[#c9a24b]" style={{ background: '#FBF9F4' }}>
                      <h3 className="text-[13px] font-bold uppercase tracking-widest" style={{ color: '#0b2545' }}>
                        {sec.heading}
                      </h3>
                    </div>
                    <ol className="px-6 py-4 space-y-2.5 print:px-0">
                      {sec.docs.map((doc) => {
                        const n = globalNum++;
                        return (
                          <li key={n} className="flex gap-3 text-[14px] text-slate-700 leading-relaxed">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full text-[12px] font-bold grid place-items-center mt-0.5" style={{ background: '#e8f3ff', color: '#1e73be' }}>
                              {n}
                            </span>
                            <span>{doc}</span>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                ));
              })()}

              {/* Additional (problem) docs */}
              {problemDocs.length > 0 && (
                <div className="border-t border-slate-100">
                  <div className="px-6 py-3 print:px-0" style={{ background: '#fff8f0' }}>
                    <h3 className="text-[13px] font-bold uppercase tracking-widest text-amber-700">
                      Additional Documents
                    </h3>
                  </div>
                  <ol className="px-6 py-4 space-y-2.5 print:px-0">
                    {problemDocs.map((doc) => {
                      const n = globalNum++;
                      return (
                        <li key={n} className="flex gap-3 text-[14px] text-slate-700 leading-relaxed">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full text-[12px] font-bold grid place-items-center mt-0.5 bg-amber-100 text-amber-700">
                            {n}
                          </span>
                          <span>{doc}</span>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </div>

            {/* Profile-specific note */}
            {profileNote && (
              <div className="mt-4 flex gap-3 rounded-xl px-5 py-4 text-[14px] text-slate-700 leading-relaxed border border-[#c9a24b]/35" style={{ backgroundColor: '#FBF9F4' }}>
                <span className="text-xl flex-shrink-0">📌</span>
                <p>{profileNote}</p>
              </div>
            )}

            {/* ── Any problem? section ── */}
            {applicableProblems.length > 0 && (
              <div className="mt-6 print:hidden">
                <button
                  onClick={() => setShowProblems((v) => !v)}
                  className="flex items-center gap-2 text-[15px] font-semibold text-slate-700 hover:text-[#1e73be] transition-colors"
                >
                  {showProblems ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Any problem with the above? Add extra documents
                </button>

                {showProblems && (
                  <div className="mt-4 card-lux px-6 py-5">
                    <p className="text-[13px] text-slate-500 mb-4">
                      Select any situations that apply — extra documents will be appended to your list automatically.
                    </p>
                    <div className="space-y-3">
                      {applicableProblems.map((prob) => (
                        <label key={prob.id} className="flex items-start gap-3 cursor-pointer group">
                          <span
                            className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 grid place-items-center transition-all ${
                              selectedProblems.includes(prob.id)
                                ? 'border-[#1e73be] bg-[#1e73be]'
                                : 'border-slate-300 bg-white group-hover:border-[#1e73be]'
                            }`}
                            onClick={() => toggleProblem(prob.id)}
                          >
                            {selectedProblems.includes(prob.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </span>
                          <div onClick={() => toggleProblem(prob.id)}>
                            <p className="text-[14px] font-medium text-slate-700">{prob.label}</p>
                            <p className="text-[12px] text-slate-400 mt-0.5">
                              Adds: {prob.docs.join('; ')}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </main>
  );
}
